import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthContextType } from '../types';
import apiService from '../services/api';
import { createSafeNotification } from '../utils/security';

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in and validate session
    const savedToken = localStorage.getItem('auth_token');
    const savedUser = localStorage.getItem('user_data');

    if (savedToken && savedUser) {
      try {
        // Validate that savedUser is valid JSON before parsing
        if (savedUser === 'undefined' || savedUser === 'null' || savedUser.trim() === '') {
          console.warn('Invalid user data in localStorage, clearing...');
          localStorage.removeItem('user_data');
          localStorage.removeItem('auth_token');
          localStorage.removeItem('refresh_token');
          setIsLoading(false);
          return;
        }

        const parsedUser = JSON.parse(savedUser);
        
        // Validate that parsed user has required structure
        if (!parsedUser || typeof parsedUser !== 'object') {
          throw new Error('Invalid user data structure');
        }

        // Validate the session by checking if the token is still valid
        validateSession(savedToken, parsedUser);
      } catch (error) {
        console.error('Error parsing user data from localStorage:', error);
        // Clear corrupted data
        localStorage.removeItem('user_data');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, []);

  const validateSession = async (token: string, user: User) => {
    try {
      // Try to get user profile to validate the token
      const response = await apiService.getProfile();
      setToken(token);
      setUser(response);
      localStorage.setItem('user_data', JSON.stringify(response));
    } catch (error) {
      console.error('Session validation failed:', error);
      // Session is invalid, clear everything
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Auto-refresh token before expiration
    let refreshInterval: NodeJS.Timeout;

    if (token) {
      // Refresh token every 50 minutes (assuming 1-hour expiration)
      refreshInterval = setInterval(async () => {
        try {
          const response = await apiService.refreshToken();
          localStorage.setItem('auth_token', response.token);
          localStorage.setItem('refresh_token', response.refresh_token);
          setToken(response.token);
        } catch (error) {
          console.error('Token refresh failed:', error);
          // If refresh fails, the session is completely invalid
          logout();
        }
      }, 50 * 60 * 1000);
    }

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [token]);

  useEffect(() => {
    // Listen for session expiration events from API service
    const handleSessionExpired = () => {
      console.log('Session expired, logging out...');
      
      // Show user-friendly notification
      showSessionExpiredNotification();
      
      logout();
    };

    // Handle network connectivity issues
    const handleOnline = () => {
      // When back online, validate session if user appears logged in
      if (token && user) {
        validateSession(token, user);
      }
    };

    window.addEventListener('auth:session-expired', handleSessionExpired);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('auth:session-expired', handleSessionExpired);
      window.removeEventListener('online', handleOnline);
    };
  }, [token, user]);

  const showSessionExpiredNotification = () => {
    const notification = createSafeNotification({
      title: 'Sessão Expirada',
      message: 'A sua sessão expirou. Por favor, faça login novamente.',
      type: 'error',
      dismissText: 'OK'
    });
    
    document.body.appendChild(notification);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (notification.parentElement) {
        notification.remove();
      }
    }, 5000);
  };

  const login = async (email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await apiService.login({ email, password });
      
      localStorage.setItem('auth_token', response.token);
      localStorage.setItem('refresh_token', response.refresh_token);
      localStorage.setItem('user_data', JSON.stringify(response.user));
      
      setToken(response.token);
      setUser(response.user);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = (): void => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_data');
    
    setToken(null);
    setUser(null);

    // Call logout API endpoint (optional, for server-side cleanup)
    apiService.logout().catch(console.error);

    // For PWA context, use React Router navigation instead of window.location
    // This will be handled by the ProtectedRoute component
  };

  const isAuthenticated = !!token && !!user;

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isAuthenticated,
    isLoading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
